'use client';

import { formatDateTime } from '@pickleballinc/lib/format-dates';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import clsx from 'clsx';
import { useMemo, useState } from 'react';

import CustomLink from '../../ui/custom-link';
import { ImageSlider } from '../../ui/image-slider';
import { TOURNAMENT_PLACEHOLDER_IMAGE } from '../utils/constants';

import { renderPlayerTourneyCardLabels, TournamentCardBadge, TournamentCardBadgeType, TournamentCardProps } from './TournamentCard';

export const MiniEventBadge = ({ children, type }: { children: React.ReactNode; type: TournamentCardBadgeType }) => {
	return (
		<TournamentCardBadge className="w-full rounded-b-none rounded-t-md" type={type} size="sm">
			{children}
		</TournamentCardBadge>
	);
};

export const PPATournamentCard = (props: TournamentCardProps) => {
	const { additionalImages, id, slug, status } = props;

	const newStatus = status ? status : renderPlayerTourneyCardLabels(props);

	return additionalImages && additionalImages?.length > 0 ? (
		<PPATournamentCardComponent {...props} status={newStatus} />
	) : (
		<CustomLink
			href={`${props.tournamentsAppBaseURL}/tournaments/${slug ? slug : id}${newStatus === 'completed' ? '/winners-circle' : ''}`}
			className="block"
		>
			<PPATournamentCardComponent {...props} status={newStatus} />
		</CustomLink>
	);
};

export const PPATournamentCardComponent = ({
	dateFrom,
	dateTo,
	logo,
	location,
	slug,
	registrationCount,
	status,
	title,
	price,
	currency,
	isTournamentsApp,
	additionalImages,
	tournamentsAppBaseURL,
	hideRegisteredPlayers
}: TournamentCardProps) => {
	// Using parseZone() to show dates in Tourney's timezone
	const formattedFromDate = formatDateTime(dateFrom, { parseZone: true });
	const formattedToDate = formatDateTime(dateTo, { parseZone: true });

	const [isPlaceholderImage, setIsPlaceholderImage] = useState(false);

	const getImageToDisplay = useMemo(() => {
		if (logo && (logo.toLowerCase().includes('/uploads/clubs/') || logo.toLowerCase().includes('/uploads/events/'))) {
			return getCDNURL(logo, 566, 384);
		}

		setIsPlaceholderImage(true);
		return getCDNURL(TOURNAMENT_PLACEHOLDER_IMAGE, 283, 192);
	}, [logo]);

	return (
		<div
			className={clsx(
				'group flex size-full select-none border rounded-xl border-gray-200',
				isTournamentsApp ? 'min-w-[215px] max-w-[215px]' : 'w-full min-[500px]:min-w-[224px] min-[500px]:max-w-[286px]'
			)}
		>
			<div className="flex size-full cursor-pointer flex-col">
				<div
					className={`relative flex items-center w-full justify-center overflow-hidden rounded-t-xl bg-white`}
					style={{ aspectRatio: '16 / 9' }}
				>
					{additionalImages && additionalImages.length > 0 ? (
						<ImageSlider
							customHref={`/tournaments/${slug}`}
							size="sm"
							containerClassNames="size-full"
							images={[getImageToDisplay, ...additionalImages]}
							objectCover
							isPlaceholderImage={isPlaceholderImage}
						/>
					) : (
						<img
							src={getImageToDisplay}
							alt={title}
							className={clsx('block h-full rounded-t-md object-cover', isPlaceholderImage && 'py-4')}
						/>
					)}
				</div>
				{additionalImages && additionalImages.length > 0 ? (
					<a
						href={`${tournamentsAppBaseURL}/tournaments/${slug}${status === 'completed' ? '/winners-circle' : ''}`}
						className={`flex flex-1 flex-col p-4 pt-3 rounded-b-xl bg-blue-900 ${isTournamentsApp ? 'group-hover:bg-blue-500' : 'group-hover:bg-gray-50'}`}
					>
						<div className="flex flex-1 flex-col gap-2">
							<p
								className={clsx(
									'min-h-[2.5rem] !line-clamp-2 text-sm font-medium text-white',
									isTournamentsApp ? 'whitespace-pre-wrap leading-5' : 'group-hover:text-gray-900'
								)}
							>
								{title}
							</p>
							<p
								className={clsx(
									'min-h-[1rem] !line-clamp-1 font-body flex items-center text-sm text-white',
									isTournamentsApp ? '' : 'group-hover:text-gray-700'
								)}
							>
								{location}
							</p>
							<p
								className={clsx(
									'!line-clamp-1 text-blue-100',
									isTournamentsApp ? 'mb-1 text-xs' : 'mb-2 text-sm group-hover:text-gray-700'
								)}
							>{`${formattedFromDate} - ${formattedToDate}`}</p>
						</div>
						<div className="flex gap-2 flex-wrap items-center justify-between pt-2 text-sm text-gray-700 md:flex-row">
							{status === 'completed' && (
								<TournamentCardBadge type="closed" className="flex-shrink-0">
									Completed
								</TournamentCardBadge>
							)}
							{status === 'registration-closed' && (
								<TournamentCardBadge type="closed" className="flex-shrink-0">
									Reg. Closed
								</TournamentCardBadge>
							)}
							{status === 'price' && (
								<TournamentCardBadge type="default" className="flex-shrink-0">
									<p
										className={clsx('text-sm', isTournamentsApp ? 'font-medium' : 'font-normal')}
									>{`${currency ? currency.toUpperCase() : ''} ${new Intl.NumberFormat('en-US', {
										style: 'currency',
										currency,
										currencyDisplay: 'narrowSymbol'
									}).format(price)}`}</p>
								</TournamentCardBadge>
							)}
							{status === 'cost-per-event' && <TournamentCardBadge type="default">Cost Per Event</TournamentCardBadge>}
							{status === 'free' && <TournamentCardBadge type="default">Free</TournamentCardBadge>}
							{!hideRegisteredPlayers && (
								<div className="my-0">
									<span
										className={`text-xs text-white ${isTournamentsApp ? '' : 'group-hover:text-gray-600'}`}
									>{`${registrationCount} Players`}</span>
								</div>
							)}
						</div>
					</a>
				) : (
					<div
						className={`flex flex-1 flex-col p-4 pt-3 rounded-b-xl bg-blue-900 ${isTournamentsApp ? 'group-hover:bg-blue-500' : 'group-hover:bg-gray-50'}`}
					>
						<div className="flex flex-1 flex-col gap-2">
							<p
								className={clsx(
									'!line-clamp-2 min-h-[2.5rem] text-sm font-medium text-white',
									isTournamentsApp ? 'whitespace-pre-wrap leading-5' : 'group-hover:text-gray-900'
								)}
							>
								{title}
							</p>
							<p
								className={clsx(
									'min-h-[1rem] !line-clamp-1 font-body flex items-center text-sm text-white',
									isTournamentsApp ? '' : 'group-hover:text-gray-600'
								)}
							>
								{location}
							</p>
							<p
								className={clsx(
									'!line-clamp-1 text-blue-100',
									isTournamentsApp ? 'mb-1 text-xs' : 'mb-2 text-sm group-hover:text-gray-700'
								)}
							>{`${formattedFromDate} - ${formattedToDate}`}</p>
						</div>
						<div className="flex gap-2 items-center justify-between pt-2 text-sm text-gray-700 md:flex-row">
							{status === 'completed' && (
								<TournamentCardBadge type="closed" className="flex-shrink-0">
									Completed
								</TournamentCardBadge>
							)}
							{status === 'registration-closed' && (
								<TournamentCardBadge type="closed" className="flex-shrink-0">
									Reg. Closed
								</TournamentCardBadge>
							)}
							{status === 'price' && (
								<TournamentCardBadge type="default" className="flex-shrink-0">
									<p
										className={clsx('text-sm', isTournamentsApp ? 'font-medium' : 'font-normal')}
									>{`${currency ? currency.toUpperCase() : ''} ${new Intl.NumberFormat('en-US', {
										style: 'currency',
										currency,
										currencyDisplay: 'narrowSymbol'
									}).format(price)}`}</p>
								</TournamentCardBadge>
							)}
							{status === 'cost-per-event' && (
								<TournamentCardBadge type="default" className="flex-shrink-0">
									Cost Per Event
								</TournamentCardBadge>
							)}
							{status === 'free' && (
								<TournamentCardBadge type="default" className="flex-shrink-0">
									Free
								</TournamentCardBadge>
							)}
							{!hideRegisteredPlayers && (
								<span className={`min-w-0 truncate text-xs text-white ${isTournamentsApp ? '' : 'group-hover:text-gray-600'}`}>
									{`${registrationCount} Players`}
								</span>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
